import React, { useState, useEffect } from 'react';
import { FaTrash, FaEye, FaPlus, FaSearch, FaSpinner } from 'react-icons/fa';
import Swal from 'sweetalert2';
import HeaderTopSection from "../../components/partials/HeaderTop";
import UserDetailModal from "../../components/modals/user/UserDetailModal";
import CreateUserModal from "../../components/modals/user/CreateUserModal";
import UserService from '../../services/UserService';
import LoadingSpinner from '../../utils/LoadingSpinner';
import '../../assets/css/UserManagementPage.css';
import Banner from "../../components/partials/Banner";

const UserManagementPage = () => {
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showLoadingSpinner, setShowLoadingSpinner] = useState(true);
    const [error, setError] = useState(null);
    const [loadingStartTime, setLoadingStartTime] = useState(null);

    // Modification : Afficher 6 éléments par page
    const itemsPerPage = 6;

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        const startTime = Date.now();
        setLoadingStartTime(startTime);
        setShowLoadingSpinner(true);
        setIsLoading(true);
        setError(null);

        try {
            // Récupération d'un grand nombre d'éléments pour gérer la pagination et la recherche localement
            const pageable = {
                page: 0,
                size: 100000,
                sort: 'lastName,asc'
            };

            const response = await UserService.getAllUsers(pageable);
            setUsers(response.content);

            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                setShowLoadingSpinner(false);
                setIsLoading(false);
            }, remainingTime);

        } catch (err) {
            const elapsedTime = Date.now() - loadingStartTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                setError(err.message || 'Erreur lors du chargement des utilisateurs');
                setShowLoadingSpinner(false);
                setIsLoading(false);
                Swal.fire(
                    'Erreur !',
                    'Impossible de charger les utilisateurs. Veuillez réessayer.',
                    'error'
                );
            }, remainingTime);
        }
    };

    const truncateText = (text, maxLength) => {
        if (!text) return '';
        if (text.length <= maxLength) {
            return text;
        }
        return text.substring(0, maxLength) + '...';
    };

    const filteredUsers = users.filter(user => {
        const hasOnlyProfessionalAndClientRoles = user.roles?.length === 2 &&
            user.roles.some(role => role.name === "PROFESSIONAL") &&
            user.roles.some(role => role.name === "CLIENT");

        const hasOnlyProfessionalRole = user.roles?.length === 1 &&
            user.roles.some(role => role.name === "PROFESSIONAL");

        const hasOnlyClientRole = user.roles?.length === 1 &&
            user.roles.some(role => role.name === "CLIENT");

        const shouldExcludeUser = hasOnlyProfessionalAndClientRoles ||
            hasOnlyProfessionalRole ||
            hasOnlyClientRole;

        return !shouldExcludeUser &&
            (`${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.roles?.some(role => role.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.phoneNumber?.includes(searchTerm));
    });

    // Calcul de la pagination locale
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

    // Sécurité : si on est sur une page qui n'existe plus (ex: après suppression), on revient à la précédente
    useEffect(() => {
        if (currentPage >= totalPages && totalPages > 0) {
            setCurrentPage(totalPages - 1);
        } else if (totalPages === 0 && currentPage !== 0) {
            setCurrentPage(0);
        }
    }, [totalPages, currentPage]);

    const startIndex = currentPage * itemsPerPage;
    const currentUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);

    const paginate = (pageNumber) => {
        if (pageNumber >= 0 && pageNumber < totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const handleDelete = async (userId) => {
        const userToDelete = users.find(user => user.id === userId);

        const result = await Swal.fire({
            title: 'Confirmer la suppression',
            html: `Êtes-vous sûr de vouloir supprimer l'utilisateur <b>${userToDelete.firstName} ${userToDelete.lastName}</b> ?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            const startTime = Date.now();
            setLoadingStartTime(startTime);
            setShowLoadingSpinner(true);

            try {
                await UserService.deleteUser(userId);
                await fetchUsers();

                const elapsedTime = Date.now() - startTime;
                const remainingTime = Math.max(3000 - elapsedTime, 0);

                setTimeout(() => {
                    setShowLoadingSpinner(false);
                    Swal.fire(
                        'Supprimé !',
                        'L\'utilisateur a été supprimé avec succès.',
                        'success'
                    );
                }, remainingTime);

            } catch (err) {
                const elapsedTime = Date.now() - startTime;
                const remainingTime = Math.max(3000 - elapsedTime, 0);

                setTimeout(() => {
                    setShowLoadingSpinner(false);
                    Swal.fire(
                        'Erreur !',
                        'La suppression a échoué. Veuillez réessayer.',
                        'error'
                    );
                }, remainingTime);
            }
        }
    };

    const handleViewDetails = (userId) => {
        const user = users.find(u => u.id === userId);
        setSelectedUser(user);
        setIsModalOpen(true);
    };

    const handleSaveUser = async (userId, updatedUserData) => {
        const startTime = Date.now();
        setLoadingStartTime(startTime);
        setShowLoadingSpinner(true);

        try {
            await UserService.updateUser(userId, updatedUserData);
            await fetchUsers();
            setIsModalOpen(false);

            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                setShowLoadingSpinner(false);
                Swal.fire(
                    'Modification réussie !',
                    'Les informations de l\'utilisateur ont été mises à jour.',
                    'success'
                );
            }, remainingTime);

        } catch (err) {
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                setShowLoadingSpinner(false);
                console.error('Error updating user:', err);
                Swal.fire(
                    'Erreur !',
                    err.response?.data?.message || 'La modification a échoué. Veuillez réessayer.',
                    'error'
                );
            }, remainingTime);
        }
    };

    const handleAddUser = () => {
        setIsCreateModalOpen(true);
    };

    const handleCreateUser = async (newUser) => {
        const startTime = Date.now();
        setLoadingStartTime(startTime);
        setShowLoadingSpinner(true);

        try {
            await fetchUsers(); // Recharger la liste des utilisateurs
            setIsCreateModalOpen(false);

            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                setShowLoadingSpinner(false);
                Swal.fire(
                    'Utilisateur créé !',
                    'Le nouvel utilisateur a été ajouté avec succès.',
                    'success'
                );
            }, remainingTime);

        } catch (err) {
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                setShowLoadingSpinner(false);
                Swal.fire(
                    'Erreur !',
                    'La création a échoué. Veuillez réessayer.',
                    'error'
                );
            }, remainingTime);
        }
    };

    // Calcul des statistiques
    const userStats = {
        total: filteredUsers.length,
        active: filteredUsers.filter(user => user.status === 'ACTIVE').length,
        inactive: filteredUsers.filter(user => user.status === 'INACTIVE').length
    };

    return (
        <>
            <HeaderTopSection />
            <Banner
                title="Gestion des utilisateurs"
                backgroundImage="https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
            />

            {/* LoadingSpinner personnalisé */}
            <LoadingSpinner
                isVisible={showLoadingSpinner}
                loadingText="Chargement des utilisateurs..."
            />

            <div className="user-management-page">
                <div className="search-container">
                    <div className="search-bar">
                        <div className="search-icon"><FaSearch /></div>
                        <input
                            type="text"
                            placeholder="Rechercher par nom, email ou téléphone..."
                            className="search-input"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(0); // Retour à la page 1 lors d'une recherche
                            }}
                        />
                    </div>
                    <button className="add-button" onClick={handleAddUser}>
                        <FaPlus /> Ajouter
                    </button>
                </div>

                {isLoading && !showLoadingSpinner ? (
                    <div className="loading-container">
                        <FaSpinner className="spinner" />
                        <p>Chargement des utilisateurs...</p>
                    </div>
                ) : error ? (
                    <div className="error-container">
                        <p className="error-message">{error}</p>
                        <button onClick={fetchUsers} className="retry-button">
                            Réessayer
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="user-list">
                            {currentUsers.length > 0 ? (
                                currentUsers.map((user) => (
                                    <div key={user.id} className="user-card" onClick={() => handleViewDetails(user.id)}>
                                        <div className={`status-badge ${user.status?.toLowerCase() || 'full'}`}>
                                            {user.roles?.map(role => role.name).join(', ') || 'Aucun rôle'}
                                        </div>
                                        <div className="user-image-container">
                                            <img
                                                src="/assets/images/team/avatar.png"
                                                alt={`${user.firstName} ${user.lastName}`}
                                                className="user-image"
                                            />
                                        </div>
                                        <div className="user-info">
                                            <h3>{user.firstName} {user.lastName}</h3>
                                            <div className="user-email">
                                                {truncateText(user.email, 25)}
                                            </div>
                                            {user.phoneNumber && (
                                                <p>📞 {user.phoneNumber}</p>
                                            )}
                                        </div>
                                        <div className="user-controls">
                                            <button
                                                className="control-icon blue"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewDetails(user.id);
                                                }}
                                                title="Voir détails"
                                            >
                                                <FaEye />
                                            </button>
                                            <button
                                                className="control-icon pink"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDelete(user.id);
                                                }}
                                                title="Supprimer"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="no-results">
                                    Aucun utilisateur trouvé
                                    <br />
                                    <small>Essayez de modifier vos critères de recherche</small>
                                </div>
                            )}
                        </div>

                        {totalPages > 1 && (
                            <div className="pagination">
                                <button
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 0}
                                >
                                    Précédent
                                </button>
                                {Array.from({ length: totalPages }, (_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => paginate(index)}
                                        className={currentPage === index ? 'active' : ''}
                                    >
                                        {index + 1}
                                    </button>
                                ))}
                                <button
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages - 1}
                                >
                                    Suivant
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {isModalOpen && selectedUser && (
                <UserDetailModal
                    user={selectedUser}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSaveUser}
                />
            )}

            {isCreateModalOpen && (
                <CreateUserModal
                    onClose={() => setIsCreateModalOpen(false)}
                    onCreate={handleCreateUser}
                />
            )}
        </>
    );
};

export default UserManagementPage;